import { type IconType } from 'react-icons';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { MinusIcon, AddIcon } from '@chakra-ui/icons';
import {
  MdDirectionsCar,
  MdHome,
  MdLocalShipping,
  MdStorefront,
  MdVerifiedUser,
} from 'react-icons/md';

import Card from 'components/card/Card';
import { LOB_PERSONAL_AUTO, LOB_PERSONAL_PROPERTY } from 'variables/constants';
import type { SubmissionRoute } from 'api/quote-marketplace';

interface StartQuoteLine {
  key: string;
  label: string;
  icon: IconType;
  /** `null` marks a line that is mocked up but not built yet. */
  lineOfBusiness: number | null;
  /** PL Specialty / CL Property workflow. */
  route?: SubmissionRoute;
}

const START_QUOTE_LINES: StartQuoteLine[] = [
  {
    key: 'pl-auto',
    label: 'PL Auto',
    icon: MdDirectionsCar,
    lineOfBusiness: LOB_PERSONAL_AUTO,
  },
  {
    key: 'pl-property',
    label: 'PL Property',
    icon: MdHome,
    lineOfBusiness: LOB_PERSONAL_PROPERTY,
  },
  {
    key: 'pl-specialty',
    label: 'PL Specialty',
    icon: MdVerifiedUser,
    lineOfBusiness: null,
    route: 'pl_specialty',
  },
  {
    key: 'cl-auto',
    label: 'CL Auto',
    icon: MdLocalShipping,
    lineOfBusiness: null,
  },
  {
    key: 'cl-property',
    label: 'CL Property',
    icon: MdStorefront,
    lineOfBusiness: null,
    route: 'cl_property',
  },
];

export type StartQuoteSelection =
  | { lineOfBusiness: number; route?: undefined; label?: undefined }
  | { lineOfBusiness?: undefined; route: SubmissionRoute; label: string };

interface StartQuoteCardProps {
  onSelect: (selection: StartQuoteSelection) => void;
  isLoading?: boolean;
}

const StartQuoteCard = ({
  onSelect,
  isLoading = false,
}: StartQuoteCardProps) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const tileBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const tileBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const iconBg = useColorModeValue('white', 'whiteAlpha.100');
  const iconColor = useColorModeValue('brand.500', 'brand.400');
  const labelColor = useColorModeValue('gray.600', 'gray.300');

  const isAvailable = (line: StartQuoteLine) =>
    line.lineOfBusiness !== null || !!line.route;

  const renderSelect = (line: StartQuoteLine) => {
    const isComingSoon = !isAvailable(line);
    const isDisabled = isComingSoon || isLoading;

    if (isDisabled) {
      const tooltipLabel = isComingSoon
        ? `${line.label} quoting is coming soon`
        : 'Loading contact data...';

      return (
        <Tooltip label={tooltipLabel} hasArrow>
          <Box width="100%">
            <Button size="sm" variant="outline" width="100%" isDisabled>
              Select
            </Button>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Button
        size="sm"
        variant="outline"
        width="100%"
        onClick={() =>
          onSelect(
            line.route
              ? { route: line.route, label: line.label }
              : { lineOfBusiness: line.lineOfBusiness as number },
          )
        }
      >
        Select
      </Button>
    );
  };

  return (
    <Card px="20px" py="16px">
      <Flex justify="space-between" align="center" mb={isOpen ? 4 : 0}>
        <Text color={textColor} fontSize="lg" fontWeight="700">
          Start Quote
        </Text>
        <IconButton
          aria-label={isOpen ? 'Collapse start quote' : 'Expand start quote'}
          icon={isOpen ? <MinusIcon /> : <AddIcon />}
          size="xs"
          variant="ghost"
          onClick={onToggle}
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={3}>
          {START_QUOTE_LINES.map((line) => {
            const isComingSoon = !isAvailable(line);
            return (
              <Flex
                key={line.key}
                direction="column"
                align="center"
                bg={tileBg}
                border="1px solid"
                borderColor={tileBorder}
                rounded="lg"
                p={3}
                opacity={isComingSoon ? 0.6 : 1}
              >
                <Flex
                  align="center"
                  justify="center"
                  boxSize="36px"
                  rounded="full"
                  bg={iconBg}
                  mb={2}
                >
                  <Icon as={line.icon} boxSize="18px" color={iconColor} />
                </Flex>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  color={labelColor}
                  whiteSpace="nowrap"
                >
                  {line.label}
                </Text>
                {/* Accent bar only; the divider below it stays neutral. Same
                    orange as the client card so the view reads as one palette. */}
                <Box
                  w="32px"
                  h="2px"
                  bg="#E8683A"
                  rounded="full"
                  mt={1}
                  mb={2}
                />
                <Divider mb={3} borderColor={tileBorder} />
                {renderSelect(line)}
              </Flex>
            );
          })}
        </SimpleGrid>
      </Collapse>
    </Card>
  );
};

export default StartQuoteCard;
